;(function (global) {
  const currentScriptOrigin = (() => {
    try {
      return document.currentScript?.src
        ? new URL(document.currentScript.src).origin
        : null
    } catch {
      return null
    }
  })()

  class EventEmitter {
    #callbacks = {}

    static Event = class {
      type = ''
      target = ''
      data = ''
    }

    addEventListener(eventType, callback) {
      if (typeof callback !== 'function') return
      if (this.#callbacks[eventType] === undefined) {
        this.#callbacks[eventType] = []
      }

      this.#callbacks[eventType].push(callback)
    }

    removeEventListener(eventType) {
      delete this.#callbacks[eventType]
    }

    removeAllEventListeners(_eventType) {
      this.#callbacks = {}
    }

    dispatchEvent(eventType, data) {
      if (this.#callbacks[eventType] === undefined) return

      const event = new EventEmitter.Event()
      event.type = eventType
      event.target = this
      event.data = data

      this.#callbacks[eventType].forEach((callback) => {
        callback(event)
      })
    }
  }

  class CirrusReader extends EventEmitter {
    constructor({
      instanceId,
      origin,
      configVersion,
      config = {},
      container,
      sessionToken,
      readerAccess,
      authEndpoint
    }) {
      super()
      if (!instanceId) throw new Error('CirrusReader: Missing instanceId')
      if (!container) throw new Error('CirrusReader: Missing container element')
      console.info(
        '[CirrusReader] Waiting to configure Cirrus Reader instance.'
      )

      this.performMessageListeningFunction = null
      this.configVersion = configVersion
      this.instanceId = instanceId
      this.apiOrigin = (origin || currentScriptOrigin)?.replace(/\/$/, '')

      if (!this.apiOrigin) {
        throw new Error(
          'CirrusReader: Missing origin and unable to determine it from the plugin script URL'
        )
      }

      this.origin = `${this.apiOrigin}/${encodeURIComponent(instanceId)}`
      this.config = config
      this.container = container
      this.iframe = null
      this.sessionToken = sessionToken
      this.readerAccess = readerAccess
      this.authEndpoint = authEndpoint

      this._bindMessageListener()
    }

    initialise(height = '100%') {
      this.dispatchEvent('readerLoading', {})
      if (this.configVersion) {
        this.origin = `${this.origin}?config=${btoa(this.configVersion)}`
        this._initIframe(height)
        return
      }

      this._checkConfig()
        .then((result) => {
          this.configVersion = this.configVersion || result.lastUpdated
          this.origin = `${this.origin}?config=${btoa(this.configVersion)}`
          this._initIframe(height)
        })
        .catch((error) => {
          this._renderError(error)
          this.dispatchEvent('readerError', {
            message: error.message,
            status: error.status
          })
        })
    }

    async _checkConfig() {
      const response = await fetch(
        `${this.apiOrigin}/api/config/${this.instanceId}`
      )
      const config = await response.json().catch(() => ({}))

      if (!response.ok) {
        const message =
          config?.statusMessage ||
          config?.message ||
          (response.status === 404
            ? 'Reader Instance not found'
            : 'Unable to load reader instance')
        const error = new Error(message)
        error.status = response.status
        throw error
      }

      if (!config.lastUpdated) {
        const error = new Error('Invalid reader instance configuration')
        error.status = response.status
        throw error
      }

      return {
        lastUpdated: config.lastUpdated
      }
    }

    _renderError(error) {
      const message = error?.message || 'Unable to load reader instance'
      const wrapper = document.createElement('div')
      const content = document.createElement('div')
      const title = document.createElement('div')
      const detail = document.createElement('div')

      wrapper.setAttribute('role', 'alert')
      wrapper.style.boxSizing = 'border-box'
      wrapper.style.display = 'grid'
      wrapper.style.minHeight = '180px'
      wrapper.style.placeItems = 'center'
      wrapper.style.margin = '24px 12px 12px'
      wrapper.style.width = 'calc(100% - 24px)'
      wrapper.style.border = '1px solid #e4e7ec'
      wrapper.style.borderRadius = '8px'
      wrapper.style.background =
        'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)'
      wrapper.style.color = '#101828'
      wrapper.style.fontFamily =
        'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
      wrapper.style.padding = '28px'

      content.style.maxWidth = '360px'
      content.style.textAlign = 'center'

      title.style.color = '#101828'
      title.style.fontSize = '16px'
      title.style.fontWeight = '650'
      title.style.lineHeight = '1.4'
      title.textContent = message

      detail.style.color = '#667085'
      detail.style.fontSize = '14px'
      detail.style.lineHeight = '1.5'
      detail.style.marginTop = '6px'
      detail.textContent = 'This reader instance could not be loaded.'

      content.appendChild(title)
      content.appendChild(detail)
      wrapper.appendChild(content)

      this.container.replaceChildren(wrapper)
    }

    _initIframe(height) {
      this.iframe = document.createElement('iframe')
      this.iframe.title = 'Cirrus Reader - Powered by Colibrio'
      this.iframe.src = `${this.origin}`
      this.iframe.style.flex = '1 1 auto'
      this.iframe.style.display = 'block'
      this.iframe.style.width = '100%'
      this.iframe.style.height = height
      this.iframe.style.border = '0'
      this.iframe.allow = 'fullscreen'
      this.iframe.tabIndex = 0
      this.iframe.setAttribute('data-cirrus-embed', '')
      this.container.style.display = 'flex'
      this.container.style.flexDirection = 'column'
      this.container.appendChild(this.iframe)
      this.dispatchEvent('readerLoaded', {})
    }

    _bindMessageListener() {
      this.performMessageListeningFunction =
        this._performMessageListening.bind(this)
      window.addEventListener('message', this.performMessageListeningFunction)
    }

    _unbindMessageListener() {
      window.removeEventListener(
        'message',
        this.performMessageListeningFunction
      )
    }

    _performMessageListening(event) {
      if (
        !this.iframe ||
        event.source !== this.iframe.contentWindow ||
        event.origin !== this.apiOrigin
      )
        return

      const { type, payload } = event.data || {}
      if (!type) return
      switch (type) {
        case 'cirrus-reader-ready':
          {
            this.send('init-cirrus-reader', {
              configData: this.readerAccess
                ? {
                    ...this.config,
                    publication: {
                      ...this.config.publication,
                      id: this.readerAccess.publicationId
                    }
                  }
                : this.config,
              instanceId: this.instanceId,
              session: this.sessionToken,
              readerAccess: this.readerAccess,
              authEndpoint: this.authEndpoint
            })
            this.dispatchEvent('readerReady', {
              payload,
              configData: this.config,
              instanceId: this.instanceId
            })
          }
          break
        case 'cirrus-reader-publication-loaded':
          {
            this.focus()
            this.dispatchEvent('readerPublicationLoaded', {
              payload,
              configData: this.config,
              instanceId: this.instanceId
            })
          }
          break
        case 'cirrus-reader-heartbeat':
          {
            this.dispatchEvent('readerHeartbeat', {
              payload,
              configData: this.config,
              instanceId: this.instanceId
            })
          }
          break
      }
    }

    _postMessage(type, payload) {
      if (this.iframe && this.iframe.contentWindow) {
        this.iframe.contentWindow.postMessage({ type, payload }, this.apiOrigin)
      }
    }

    send(type, payload) {
      this._postMessage(type, payload)
    }

    focus() {
      if (!this.iframe) return
      this.iframe.focus({ preventScroll: true })
    }

    destroy() {
      this._unbindMessageListener()
      this.removeAllEventListeners()
      if (this.iframe && this.iframe.parentNode) {
        this.iframe.parentNode.removeChild(this.iframe)
      }
      this.dispatchEvent('readerDestroyed', {})
    }

    getConfig() {
      return this.config
    }
  }
  // Expose globally
  global.CirrusReader = CirrusReader
})(window)
